import { apiClient, ApiClient } from './apiClient';
import { AuthServiceUAU } from './authServiceUAU';
import type {
  EstadoOrdenCarga,
  OrdenCargaCombustible,
  OrdenCargaCombustibleCreateRequest,
  OrdenCargaCombustibleUpdateRequest,
  OrdenCargaCombustibleSolicitarRequest,
  OrdenCargaCombustibleEmitirRequest,
  OrdenCargaCombustibleFiltros,
  OrdenCargaCombustiblePaginatedResponse,
  DestinoPorRevisar,
} from '../types/ordenesCargaCombustible';

export interface CargarOrdenParams {
  cantidadCargada: number;
  rutCargador: string;
  nombreCargador: string;
  fotoGuiaDespacho: File;
  horometroDeclarado?: number;
  nivelDeclarado?: number;
  // Opcional: foto del horómetro + nivel de estanque, mismo concepto que la
  // foto de evidencia de Control de Combustible (respalda visualmente
  // horometroDeclarado/nivelDeclarado).
  fotoEvidenciaNivel?: File;
}

export interface RechazarDestinoParams {
  ordenId: number;
  destinoId: number;
  observacionesRevision: string;
}

export interface RegularizarCargaParams {
  cantidadCargada?: number;
  rutCargador?: string;
  nombreCargador?: string;
  horometroDeclarado?: number;
  nivelDeclarado?: number;
  // Reemplazos opcionales — solo si el supervisor vuelve a fotografiar.
  fotoGuiaDespacho?: File;
  fotoEvidenciaNivel?: File;
}

class OrdenesCargaCombustibleService {
  async listar(filtros: OrdenCargaCombustibleFiltros = {}): Promise<OrdenCargaCombustiblePaginatedResponse> {
    const response = await apiClient.get<OrdenCargaCombustiblePaginatedResponse>('/ordenes-carga-combustible', {
      params: {
        page: filtros.page ?? 1,
        per_page: filtros.per_page ?? 20,
        estado: filtros.estado,
        fecha_inicio: filtros.fecha_inicio,
        fecha_fin: filtros.fecha_fin,
        proveedor_id: filtros.proveedor_id,
        vehiculo_id: filtros.vehiculo_id,
        maquina_id: filtros.maquina_id,
        operador_id: filtros.operador_id,
      },
    });
    return response.data;
  }

  async mias(estado?: EstadoOrdenCarga, page = 1, perPage = 20): Promise<OrdenCargaCombustiblePaginatedResponse> {
    const response = await apiClient.get<OrdenCargaCombustiblePaginatedResponse>(
      '/ordenes-carga-combustible/mias',
      { params: { page, per_page: perPage, estado } }
    );
    return response.data;
  }

  async obtener(id: number): Promise<OrdenCargaCombustible> {
    const response = await apiClient.get<OrdenCargaCombustible>(`/ordenes-carga-combustible/${id}`);
    return response.data;
  }

  async solicitar(datos: OrdenCargaCombustibleSolicitarRequest): Promise<OrdenCargaCombustible> {
    const response = await apiClient.post<OrdenCargaCombustible>('/ordenes-carga-combustible/solicitar', datos);
    return response.data;
  }

  /** Autoriza una solicitud (SOLICITADA -> AUTORIZADA). Recién una orden
   * AUTORIZADA puede emitirse. */
  async autorizar(id: number): Promise<OrdenCargaCombustible> {
    const response = await apiClient.post<OrdenCargaCombustible>(`/ordenes-carga-combustible/${id}/autorizar`);
    return response.data;
  }

  async emitir(id: number, datos: OrdenCargaCombustibleEmitirRequest): Promise<OrdenCargaCombustible> {
    const response = await apiClient.post<OrdenCargaCombustible>(`/ordenes-carga-combustible/${id}/emitir`, datos);
    return response.data;
  }

  /** Sugiere el siguiente N° de orden correlativo de la organización. */
  async siguienteNumero(): Promise<string> {
    const response = await apiClient.get<{ siguiente_numero: string }>(
      '/ordenes-carga-combustible/siguiente-numero'
    );
    return response.data.siguiente_numero;
  }

  async crear(datos: OrdenCargaCombustibleCreateRequest): Promise<OrdenCargaCombustible> {
    const response = await apiClient.post<OrdenCargaCombustible>('/ordenes-carga-combustible', datos);
    return response.data;
  }

  async actualizar(id: number, datos: OrdenCargaCombustibleUpdateRequest): Promise<OrdenCargaCombustible> {
    const response = await apiClient.put<OrdenCargaCombustible>(`/ordenes-carga-combustible/${id}`, datos);
    return response.data;
  }

  async eliminar(id: number): Promise<void> {
    await apiClient.delete(`/ordenes-carga-combustible/${id}`);
  }

  async cargar(id: number, destinoId: number, params: CargarOrdenParams): Promise<OrdenCargaCombustible> {
    const formData = new FormData();
    formData.append('cantidad_cargada', String(params.cantidadCargada));
    if (params.rutCargador) formData.append('rut_cargador', params.rutCargador);
    if (params.nombreCargador) formData.append('nombre_cargador', params.nombreCargador);
    formData.append('foto_guia_despacho', params.fotoGuiaDespacho);
    if (params.horometroDeclarado != null) {
      formData.append('horometro_declarado', String(params.horometroDeclarado));
    }
    if (params.nivelDeclarado != null) {
      formData.append('nivel_declarado', String(params.nivelDeclarado));
    }
    if (params.fotoEvidenciaNivel) {
      formData.append('foto_evidencia_nivel', params.fotoEvidenciaNivel);
    }

    const response = await apiClient.post<OrdenCargaCombustible>(
      `/ordenes-carga-combustible/${id}/destinos/${destinoId}/cargar`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  /** Corrección del supervisor sobre una carga ya realizada (regularización).
   * Todos los campos son opcionales: se envía solo lo que cambia. Reabre el
   * destino a revisión (vuelve a CARGADA). */
  async regularizarCarga(ordenId: number, destinoId: number, params: RegularizarCargaParams): Promise<OrdenCargaCombustible> {
    const formData = new FormData();
    if (params.cantidadCargada != null) formData.append('cantidad_cargada', String(params.cantidadCargada));
    if (params.rutCargador != null && params.rutCargador.trim() !== '') formData.append('rut_cargador', params.rutCargador.trim());
    if (params.nombreCargador != null && params.nombreCargador.trim() !== '') formData.append('nombre_cargador', params.nombreCargador.trim());
    if (params.horometroDeclarado != null) formData.append('horometro_declarado', String(params.horometroDeclarado));
    if (params.nivelDeclarado != null) formData.append('nivel_declarado', String(params.nivelDeclarado));
    if (params.fotoGuiaDespacho) formData.append('foto_guia_despacho', params.fotoGuiaDespacho);
    if (params.fotoEvidenciaNivel) formData.append('foto_evidencia_nivel', params.fotoEvidenciaNivel);

    const response = await apiClient.put<OrdenCargaCombustible>(
      `/ordenes-carga-combustible/${ordenId}/destinos/${destinoId}/regularizar-carga`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  async enviarPorCorreo(id: number): Promise<OrdenCargaCombustible> {
    const response = await apiClient.post<OrdenCargaCombustible>(`/ordenes-carga-combustible/${id}/enviar-correo`);
    return response.data;
  }

  async aprobarDestino(ordenId: number, destinoId: number): Promise<OrdenCargaCombustible> {
    const response = await apiClient.post<OrdenCargaCombustible>(
      `/ordenes-carga-combustible/${ordenId}/destinos/${destinoId}/aprobar`
    );
    return response.data;
  }

  async rechazarDestino(params: RechazarDestinoParams): Promise<OrdenCargaCombustible> {
    const response = await apiClient.post<OrdenCargaCombustible>(
      `/ordenes-carga-combustible/${params.ordenId}/destinos/${params.destinoId}/rechazar`,
      { observaciones_revision: params.observacionesRevision }
    );
    return response.data;
  }

  /** Rechaza una SOLICITADA completa (antes de emitirla) — no confundir con
   * rechazarDestino, que rechaza la carga de un destino ya emitido. */
  async rechazarSolicitud(id: number, observacionesRevision: string): Promise<OrdenCargaCombustible> {
    const response = await apiClient.post<OrdenCargaCombustible>(`/ordenes-carga-combustible/${id}/rechazar`, {
      observaciones_revision: observacionesRevision,
    });
    return response.data;
  }

  /** Fila plana orden+destino para la pantalla "Revisar cargas" — una fila
   * por línea en estado CARGADA, no una fila por orden. */
  async revisarCargas(): Promise<DestinoPorRevisar[]> {
    const response = await apiClient.get<DestinoPorRevisar[]>('/ordenes-carga-combustible/revisar-cargas');
    return response.data;
  }

  /**
   * Trae la foto de guía de despacho de un destino como blob URL. No existe
   * un mount estático de storage/, así que la foto se sirve autenticada por
   * la API (igual que descargarPdf) en vez de un <img src> directo.
   * El caller debe hacer URL.revokeObjectURL cuando ya no la use.
   */
  async obtenerFotoGuiaDespachoUrl(ordenId: number, destinoId: number): Promise<string> {
    const token = AuthServiceUAU.getToken();
    if (!token) {
      throw new Error('No hay sesión activa');
    }

    const response = await fetch(
      `/mava/api/v1/ordenes-carga-combustible/${ordenId}/destinos/${destinoId}/foto-guia-despacho`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Empresa-ID': ApiClient.getEmpresaId() || '',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Error HTTP: ${response.status}`);
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  /** Igual que obtenerFotoGuiaDespachoUrl, pero para la foto de horómetro +
   * nivel de estanque (opcional — puede no existir para un destino). */
  async obtenerFotoEvidenciaNivelUrl(ordenId: number, destinoId: number): Promise<string> {
    const token = AuthServiceUAU.getToken();
    if (!token) {
      throw new Error('No hay sesión activa');
    }

    const response = await fetch(
      `/mava/api/v1/ordenes-carga-combustible/${ordenId}/destinos/${destinoId}/foto-evidencia-nivel`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Empresa-ID': ApiClient.getEmpresaId() || '',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Error HTTP: ${response.status}`);
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  /**
   * Descarga el PDF de la orden (logo + datos + QR de verificación) y
   * dispara la descarga en el navegador. No pasa por apiClient/axios porque
   * es una descarga binaria, no JSON — mismo patrón que
   * ReporteDetalleMovilView.handleGenerarPDF.
   */
  async descargarPdf(id: number, numeroOrden?: string | null): Promise<void> {
    const token = AuthServiceUAU.getToken();
    if (!token) {
      throw new Error('No hay sesión activa');
    }

    const response = await fetch(`/mava/api/v1/ordenes-carga-combustible/${id}/pdf`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Empresa-ID': ApiClient.getEmpresaId() || '',
      },
    });

    if (!response.ok) {
      throw new Error(`Error HTTP: ${response.status}`);
    }

    const blob = await response.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `orden_${numeroOrden || id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  }
}

export const ordenesCargaCombustibleService = new OrdenesCargaCombustibleService();
export default ordenesCargaCombustibleService;
